/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { useSelector, dispatch } from '../redux/store';
import { getSettingsTemplateData } from '../redux/slices/settingsTemplate';
import { getTemplateList } from '../redux/slices/practiceMessage';
import { sendEngagementEmail } from '../redux/api/practiceMessageApis';
import { recordFeedbackSend } from '../utils/recentFeedbackSends';
import useCustomSnackbar from './useCustomSnackbar';

/**
 * Mirrors useGoogleReviewSend but targets the APPOINTMENT_REVIEW_LINK template
 * (configurable in /dashboard/user/message). Closes the orphan loop where the
 * template type existed in the settings UI but no consumer ever invoked it.
 *
 * Used by the Generate Feedback Link popup to send the link directly to the
 * patient via the configured channel (email or WhatsApp) instead of forcing
 * the practitioner to copy/paste manually.
 *
 * Returns:
 *   templateData  – resolved template detail (or null)
 *   hasTemplate   – whether the practice has APPOINTMENT_REVIEW_LINK configured
 *   isSending     – whether a send is in flight
 *   sendRequest   – fire-and-forget single-lead send
 */
export default function usePatientFeedbackSend() {
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const settingsTemplatePrefs = useSelector((state) => state.settingsTemplate.templateData);
    const practiceMessageTemplates = useSelector((state) => state.practiceMessage.templateDetails);
    const { enqueueSnackbar } = useCustomSnackbar();

    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        if (practiceId) {
            if (!settingsTemplatePrefs) dispatch(getSettingsTemplateData(practiceId));
            if (!practiceMessageTemplates?.results?.length) dispatch(getTemplateList(practiceId, true));
        }
    }, [practiceId]);

    const reviewLinkPref = settingsTemplatePrefs?.results?.find(
        (t) => t.type === 'APPOINTMENT_REVIEW_LINK'
    );
    const templateData = reviewLinkPref?.template
        ? (practiceMessageTemplates?.results?.find((t) => t.id === reviewLinkPref.template) ?? null)
        : null;
    const hasTemplate = Boolean(templateData);

    const sendRequest = ({ leadId, leadTreatmentId, appointmentId, leadData } = {}, { onSuccess } = {}) => {
        if (!reviewLinkPref?.template) {
            enqueueSnackbar(
                'No Patient Feedback template configured. Open /dashboard/user/message and set a default for "Patient feedback link".',
                { variant: 'warning' }
            );
            return;
        }
        if (!leadId || !leadTreatmentId) {
            enqueueSnackbar('Cannot send — lead or treatment is missing.', { variant: 'error' });
            return;
        }
        setIsSending(true);
        // appointment is optional on the backend but required for the
        // $appointment_feedback_url merge tag to resolve to the just-attended
        // appointment (not the next-booked auto-merged from LeadTreatment).
        const detail = {
            lead: leadId,
            lead_treatment: leadTreatmentId,
            ...(appointmentId ? { appointment: appointmentId } : {}),
        };
        const callback = (data) => {
            recordFeedbackSend({
                practiceId,
                leadId,
                leadName: leadData
                    ? `${leadData.first_name || ''} ${leadData.last_name || ''}`.trim()
                    : 'Patient',
                treatmentId: leadTreatmentId,
            });
            setTimeout(() => {
                setIsSending(false);
                enqueueSnackbar('Patient feedback link sent successfully', { variant: 'success' });
                if (onSuccess) onSuccess(data);
            }, 1500);
        };
        dispatch(sendEngagementEmail({
            id: reviewLinkPref.template,
            detail,
            callback,
            data: leadData,
            errorCallback: () => setIsSending(false),
        }));
    };

    return {
        templateData,
        hasTemplate,
        isSending,
        sendRequest,
    };
}
